import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../hooks/useStore";
import {
  UserPlus,
  Shield,
  Lock,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { toast } from "react-toastify";
import { AuthHeroSection } from "../../components/auth/AuthHeroSection";
import { PasswordForm } from "../../components/auth/PasswordForm";
import type { PasswordFormData, SetPasswordRequest } from "../../types/index";
import { setPassword, clearError } from "../../store/slices/authSlice";
import { useTranslation } from "react-i18next";

const SetPasswordPage: React.FC = () => {
  const { t } = useTranslation("auth");
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState<boolean | null>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setTokenValid(false);
      return;
    }

    // Validate token format (basic validation)
    if (token.length < 32) {
      setTokenValid(false);
      return;
    }

    setTokenValid(true);
  }, [token]);

  const onSubmit = async (formData: PasswordFormData) => {
    if (!token) return;

    setLoading(true);
    try {
      const payload: SetPasswordRequest = {
        token,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };

      await dispatch(setPassword(payload)).unwrap();
      setIsSuccess(true);
      toast.success(t("setPassword.successToast"));
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("setPassword.failedToast"),
      );
    } finally {
      setLoading(false);
      dispatch(clearError());
    }
  };

  if (tokenValid === false) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex">
        <AuthHeroSection
          brandIcon={AlertTriangle}
          brandTitle={t("setPassword.invalidInvitation")}
          mainTitle={t("setPassword.invitationExpired")}
          highlightedTitle=""
          description={t("setPassword.invitationExpiredDescription")}
          features={[
            {
              icon: Shield,
              title: t("setPassword.invalidHero.feature1Title"),
              description: t("setPassword.invalidHero.feature1Desc"),
            },
            {
              icon: UserPlus,
              title: t("setPassword.invalidHero.feature2Title"),
              description: t("setPassword.invalidHero.feature2Desc"),
            },
          ]}
          colorScheme="primary"
        />

        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md text-center">
            <div className="bg-white rounded-2xl shadow-sm border border-[rgba(11,15,14,0.08)] p-8">
              <div className="mx-auto h-16 w-16 bg-[#fdf5ec] rounded-xl flex items-center justify-center mb-6">
                <AlertTriangle className="h-8 w-8 text-[#C97A3C]" />
              </div>
              <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-4">
                {t("setPassword.invalidInvitation")}
              </h2>
              <p className="text-[#8A9590] mb-8">
                {t("setPassword.invitationExpiredMessage")}
              </p>
              <Link
                to="/login"
                className="inline-block w-full bg-[#C97A3C] hover:bg-[#B86B30] text-white py-3 rounded-lg transition-all duration-200 font-medium text-center"
              >
                {t("setPassword.backToSignIn")}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex">
        <AuthHeroSection
          brandIcon={CheckCircle}
          brandTitle={t("setPassword.successHero.brandTitle")}
          mainTitle={t("setPassword.successHero.mainTitle")}
          highlightedTitle={t("setPassword.successHero.highlightedTitle")}
          description={t("setPassword.successHero.description")}
          features={[
            {
              icon: Shield,
              title: t("setPassword.successHero.feature1Title"),
              description: t("setPassword.successHero.feature1Desc"),
            },
            {
              icon: UserPlus,
              title: t("setPassword.successHero.feature2Title"),
              description: t("setPassword.successHero.feature2Desc"),
            },
            {
              icon: Lock,
              title: t("setPassword.successHero.feature3Title"),
              description: t("setPassword.successHero.feature3Desc"),
            },
          ]}
          colorScheme="success"
        />

        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md text-center">
            <div className="bg-white rounded-2xl shadow-sm border border-[rgba(11,15,14,0.08)] p-8">
              <div className="mx-auto h-16 w-16 bg-[#e6f7ef] rounded-xl flex items-center justify-center mb-6">
                <CheckCircle className="h-8 w-8 text-[#00A862]" />
              </div>
              <h2 className="text-2xl font-medium tracking-tight text-[#0B0F0E] mb-4">
                {t("setPassword.welcomeTitle")}
              </h2>
              <p className="text-[#8A9590] mb-8">
                {t("setPassword.welcomeMessage")}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-[#C97A3C] hover:bg-[#B86B30] text-white py-3 rounded-lg transition-all duration-200 font-medium shadow-sm"
              >
                {t("setPassword.continueToSignIn")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8] flex">
      <AuthHeroSection
        brandIcon={UserPlus}
        brandTitle={t("setPassword.hero.brandTitle")}
        mainTitle={t("setPassword.hero.mainTitle")}
        highlightedTitle={t("setPassword.hero.highlightedTitle")}
        description={t("setPassword.hero.description")}
        features={[
          {
            icon: Shield,
            title: t("setPassword.hero.feature1Title"),
            description: t("setPassword.hero.feature1Desc"),
          },
          {
            icon: UserPlus,
            title: t("setPassword.hero.feature2Title"),
            description: t("setPassword.hero.feature2Desc"),
          },
          {
            icon: Lock,
            title: t("setPassword.hero.feature3Title"),
            description: t("setPassword.hero.feature3Desc"),
          },
        ]}
      />

      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden text-center mb-8">
            <div className="mx-auto flex items-center justify-center gap-3 mb-4">
              <svg width="36" height="36" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <rect x="22" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
                <rect x="27" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="32" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="38" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="44" y="18" width="4" height="64" fill="#E89D5C"/>
                <rect x="51" y="24" width="3" height="52" fill="#C97A3C" opacity="0.85"/>
                <rect x="57" y="30" width="3" height="40" fill="#C97A3C" opacity="0.7"/>
                <rect x="63" y="34" width="2" height="32" fill="#C97A3C" opacity="0.6"/>
                <rect x="68" y="38" width="2" height="24" fill="#C97A3C" opacity="0.5"/>
              </svg>
              <h1 className="text-2xl font-medium tracking-tight text-[#0B0F0E]">
                LiveCargo<span className="text-[#C97A3C]">.</span>
              </h1>
            </div>
          </div>

          <PasswordForm
            onSubmit={onSubmit}
            loading={loading}
            title={t("setPassword.title")}
            description={t("setPassword.description")}
            submitButtonText={t("setPassword.submitButton")}
          />

          <div className="mt-6 p-4 bg-[#F5F4F0] border border-[rgba(11,15,14,0.06)] rounded-lg">
            <div className="flex items-start">
              <UserPlus className="h-5 w-5 text-[#C97A3C] mt-0.5 mr-3 shrink-0" />
              <div>
                <h3 className="font-medium text-[#0B0F0E] text-sm mb-1">
                  {t("setPassword.accountInvitation")}
                </h3>
                <p className="text-[#8A9590] text-xs">
                  {t("setPassword.accountInvitationText")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPasswordPage;
